import type { ChangeEvent } from 'react';

import { HorizontalContainer } from '@american-autonomy/ag-ui';

import {
    CLEAR_BUTTON_TEST_ID,
    CLEAR_ICON_END,
    CLEAR_ICON_SIZE,
    CLEAR_ICON_START,
    CLEAR_ICON_STROKE_WIDTH,
    CLEAR_LABEL,
    DEFAULT_PLACEHOLDER,
    MAGNIFIER_CIRCLE_CX,
    MAGNIFIER_CIRCLE_CY,
    MAGNIFIER_CIRCLE_R,
    MAGNIFIER_HANDLE_X1,
    MAGNIFIER_HANDLE_X2,
    MAGNIFIER_HANDLE_Y1,
    MAGNIFIER_HANDLE_Y2,
    MAGNIFIER_SIZE,
    MAGNIFIER_STROKE_WIDTH,
    TEST_ID
} from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const SearchInput: FunctionComponent<Props> = ({
    className = '',
    onBlur,
    onChange,
    onFocus,
    placeholder = DEFAULT_PLACEHOLDER,
    testId = TEST_ID,
    value
}) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value);
    const handleClear = () => onChange('');
    const showClear = value.length > 0;

    return (
        <HorizontalContainer className={`${styles.searchInput} ${className}`} testId={testId}>
            <svg
                aria-hidden='true'
                className={styles.magnifierIcon}
                fill='none'
                height={MAGNIFIER_SIZE}
                viewBox={`0 0 ${MAGNIFIER_SIZE} ${MAGNIFIER_SIZE}`}
                width={MAGNIFIER_SIZE}
                xmlns='http://www.w3.org/2000/svg'>
                <circle
                    cx={MAGNIFIER_CIRCLE_CX}
                    cy={MAGNIFIER_CIRCLE_CY}
                    r={MAGNIFIER_CIRCLE_R}
                    stroke='currentColor'
                    strokeWidth={MAGNIFIER_STROKE_WIDTH}
                />

                <line
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeWidth={MAGNIFIER_STROKE_WIDTH}
                    x1={MAGNIFIER_HANDLE_X1}
                    x2={MAGNIFIER_HANDLE_X2}
                    y1={MAGNIFIER_HANDLE_Y1}
                    y2={MAGNIFIER_HANDLE_Y2}
                />
            </svg>

            <input
                className={styles.field}
                data-testid={`${testId}-field`}
                onBlur={onBlur}
                onChange={handleChange}
                onFocus={onFocus}
                placeholder={placeholder}
                type='text'
                value={value}
            />

            {showClear ? (
                <button
                    aria-label={CLEAR_LABEL}
                    className={styles.clearButton}
                    data-testid={CLEAR_BUTTON_TEST_ID}
                    onClick={handleClear}
                    type='button'>
                    <svg
                        aria-hidden='true'
                        className={styles.clearIcon}
                        fill='none'
                        height={CLEAR_ICON_SIZE}
                        viewBox={`0 0 ${CLEAR_ICON_SIZE} ${CLEAR_ICON_SIZE}`}
                        width={CLEAR_ICON_SIZE}
                        xmlns='http://www.w3.org/2000/svg'>
                        <line
                            stroke='currentColor'
                            strokeLinecap='round'
                            strokeWidth={CLEAR_ICON_STROKE_WIDTH}
                            x1={CLEAR_ICON_START}
                            x2={CLEAR_ICON_END}
                            y1={CLEAR_ICON_START}
                            y2={CLEAR_ICON_END}
                        />

                        <line
                            stroke='currentColor'
                            strokeLinecap='round'
                            strokeWidth={CLEAR_ICON_STROKE_WIDTH}
                            x1={CLEAR_ICON_END}
                            x2={CLEAR_ICON_START}
                            y1={CLEAR_ICON_START}
                            y2={CLEAR_ICON_END}
                        />
                    </svg>
                </button>
            ) : null}
        </HorizontalContainer>
    );
};

export default SearchInput;
